import { API_ROUTES } from "@/constants/api";
import { QUERY_KEYS } from "@/constants/config";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BundleComponentSnapshot,
  Paginated,
  ProductRow,
  ProductVariantRow,
  StockMovementRow,
} from "@bizy/core";

export interface ProductListParams {
  page?: number;
  pageSize?: number;
  q?: string;
  kind?: string;
  activeOnly?: boolean;
}

export interface ProductComponentInput {
  component_product_id: string;
  component_variant_id?: string | null;
  quantity: number;
}

const toQuery = (params: ProductListParams): string => {
  const search = new URLSearchParams();
  if (params.page) search.set("page", String(params.page));
  if (params.pageSize) search.set("pageSize", String(params.pageSize));
  if (params.q) search.set("q", params.q);
  if (params.kind) search.set("kind", params.kind);
  if (params.activeOnly) search.set("active_only", "true");
  const query = search.toString();
  return query.length > 0 ? `?${query}` : "";
};

/** Products carry the derived bundle payload; bundles have no own stock. */
export type ProductListItem = ProductRow & {
  components: BundleComponentSnapshot[];
  availability: number | null;
};

export type ProductWithVariants = ProductListItem & { variants: ProductVariantRow[] };

export const useProducts = (workspaceId: string | undefined, params: ProductListParams = {}) =>
  useQuery({
    queryKey: [QUERY_KEYS.products, workspaceId ?? "none", params],
    enabled: Boolean(workspaceId),
    queryFn: () =>
      apiGet<Paginated<ProductListItem>>(
        `${API_ROUTES.products(workspaceId as string)}${toQuery(params)}`,
      ),
  });

export const useProduct = (workspaceId: string | undefined, productId: string | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.products, workspaceId ?? "none", productId ?? "none"],
    enabled: Boolean(workspaceId && productId),
    queryFn: () =>
      apiGet<ProductWithVariants>(API_ROUTES.product(workspaceId as string, productId as string)),
  });

export const useStockMovements = (
  workspaceId: string | undefined,
  productId: string | undefined,
  page = 1,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.products, workspaceId ?? "none", productId ?? "none", "movements", page],
    enabled: Boolean(workspaceId && productId),
    queryFn: () =>
      apiGet<Paginated<StockMovementRow>>(
        `${API_ROUTES.productMovements(
          workspaceId as string,
          productId as string,
        )}?page=${page}`,
      ),
  });

const invalidateProducts = (queryClient: ReturnType<typeof useQueryClient>, workspaceId: string) =>
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.products, workspaceId] });

export const useCreateProduct = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      apiPost<ProductRow>(API_ROUTES.products(workspaceId), input),
    onSuccess: () => invalidateProducts(queryClient, workspaceId),
  });
};

export const useUpdateProduct = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: string; patch: Record<string, unknown> }) =>
      apiPatch<ProductRow>(API_ROUTES.product(workspaceId, input.productId), input.patch),
    onSuccess: () => invalidateProducts(queryClient, workspaceId),
  });
};

export const useDeleteProduct = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      apiDelete<{ deleted: boolean }>(API_ROUTES.product(workspaceId, productId)),
    onSuccess: () => invalidateProducts(queryClient, workspaceId),
  });
};

export const useCreateVariant = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: string; payload: Record<string, unknown> }) =>
      apiPost<ProductVariantRow>(
        API_ROUTES.variants(workspaceId, input.productId),
        input.payload,
      ),
    onSuccess: () => invalidateProducts(queryClient, workspaceId),
  });
};

export const useUpdateVariant = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: string; variantId: string; patch: Record<string, unknown> }) =>
      apiPatch<ProductVariantRow>(
        API_ROUTES.variant(workspaceId, input.productId, input.variantId),
        input.patch,
      ),
    onSuccess: () => invalidateProducts(queryClient, workspaceId),
  });
};

export const useDeleteVariant = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: string; variantId: string }) =>
      apiDelete<{ deleted: boolean }>(
        API_ROUTES.variant(workspaceId, input.productId, input.variantId),
      ),
    onSuccess: () => invalidateProducts(queryClient, workspaceId),
  });
};
